/**
 * Drive Settings Admin Page
 * Quản lý cấu hình Google Drive và đồng bộ tài liệu
 * With real-time sync progress and auto-stop on repeated errors
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { format } from 'date-fns'
import { vi } from 'date-fns/locale'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import {
  HiOutlineFolderOpen,
  HiOutlineRefresh,
  HiOutlineCog,
  HiOutlineCheck,
  HiOutlineExclamation,
  HiOutlineClipboardCopy,
  HiOutlineExternalLink,
  HiOutlineDatabase,
  HiOutlinePencil,
  HiOutlineFolder,
  HiOutlineDocument,
  HiOutlineStop,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineSwitchVertical,
  HiOutlinePlay,
  HiOutlinePause,
  HiOutlineLightningBolt,
  HiOutlineDocumentText,
  HiOutlineX,
} from 'react-icons/hi'
import driveService from '../../services/driveService'
import FolderTreeSelect from '../../components/drive/FolderTreeSelect'
import { useAuthStore } from '../../store/authStore'

// Cấu hình auto-stop khi lỗi
const MAX_CONSECUTIVE_ERRORS = 3
const ERROR_RESET_INTERVAL = 30000 // 30 giây không lỗi thì reset counter

function DriveSettings() {
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [paused, setPaused] = useState(false)  // Trạng thái tạm dừng
  const [canResume, setCanResume] = useState(false)  // Có thể tiếp tục không
  const [formData, setFormData] = useState({
    root_folder_id: ''
  })

  // Sync progress state
  const [syncProgress, setSyncProgress] = useState({
    percent: 0,
    current: 0,
    total: 0,
    currentItem: '',
    logs: []
  })
  const eventSourceRef = useRef(null)
  const logContainerRef = useRef(null)

  // Error tracking for auto-stop
  const consecutiveErrorsRef = useRef(0)
  const lastErrorTimeRef = useRef(null)

  // Project ordering state
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const [sortMode, setSortMode] = useState('manual') // 'manual' | 'number' | 'name'

  // Quick sync state
  const [folderTree, setFolderTree] = useState([])
  const [loadingFolderTree, setLoadingFolderTree] = useState(false)
  const [selectedFolderId, setSelectedFolderId] = useState(null)
  const [selectedFolderName, setSelectedFolderName] = useState('')
  const quickSyncEventSourceRef = useRef(null)

  // Project descriptions state
  const [projectDescriptions, setProjectDescriptions] = useState([])
  const [loadingDescriptions, setLoadingDescriptions] = useState(false)
  const [editingDescriptionId, setEditingDescriptionId] = useState(null)
  const [editingDescriptionText, setEditingDescriptionText] = useState('')
  const [savingDescription, setSavingDescription] = useState(false)

  // Auth store for token
  const { accessToken } = useAuthStore()

  // Load config
  const loadConfig = useCallback(async () => {
    setLoading(true)
    try {
      const response = await driveService.getConfig()
      if (response.status === 'success' && response.data) {
        setConfig(response.data)
        setFormData({
          root_folder_id: response.data.root_folder_id || ''
        })
      }
    } catch (error) {
      console.error('Failed to load config:', error)
      toast.error('Không thể tải cấu hình')
    } finally {
      setLoading(false)
    }
  }, [])

  // Load pause status
  const loadPauseStatus = useCallback(async () => {
    try {
      const response = await driveService.getPauseStatus()
      if (response.status === 'success' && response.data) {
        setPaused(response.data.is_paused)
        setCanResume(response.data.can_resume)
      }
    } catch (error) {
      console.error('Failed to load pause status:', error)
    }
  }, [])

  // Load projects for ordering
  const loadProjects = useCallback(async () => {
    setLoadingProjects(true)
    try {
      const response = await driveService.getProjects(sortMode)
      if (response.status === 'success') {
        setProjects(response.data || [])
      }
    } catch (error) {
      console.error('Failed to load projects:', error)
    } finally {
      setLoadingProjects(false)
    }
  }, [sortMode])

  // Load folder tree for quick sync
  const loadFolderTree = useCallback(async () => {
    setLoadingFolderTree(true)
    try {
      const response = await driveService.getFolderTree(2)
      if (response.status === 'success') {
        setFolderTree(response.data || [])
      }
    } catch (error) {
      console.error('Failed to load folder tree:', error)
    } finally {
      setLoadingFolderTree(false)
    }
  }, [])

  // Load project descriptions
  const loadProjectDescriptions = useCallback(async () => {
    setLoadingDescriptions(true)
    try {
      const response = await driveService.getProjectDescriptions()
      if (response.status === 'success') {
        setProjectDescriptions(response.data || [])
      }
    } catch (error) {
      console.error('Failed to load project descriptions:', error)
    } finally {
      setLoadingDescriptions(false)
    }
  }, [])

  // Move project up in order
  const moveProjectUp = (index) => {
    if (index === 0) return
    const newProjects = [...projects]
    const temp = newProjects[index - 1]
    newProjects[index - 1] = newProjects[index]
    newProjects[index] = temp
    setProjects(newProjects)
  }

  // Move project down in order
  const moveProjectDown = (index) => {
    if (index === projects.length - 1) return
    const newProjects = [...projects]
    const temp = newProjects[index + 1]
    newProjects[index + 1] = newProjects[index]
    newProjects[index] = temp
    setProjects(newProjects)
  }

  // Save project order
  const saveProjectOrder = async () => {
    setSavingOrder(true)
    try {
      const items = projects.map((p, index) => ({
        drive_id: p.drive_id,
        display_order: index
      }))
      const response = await driveService.reorderProjects(items)
      if (response.status === 'success') {
        toast.success('Đã lưu thứ tự!')
      } else {
        toast.error(response.message || 'Lưu thất bại')
      }
    } catch (error) {
      toast.error('Có lỗi xảy ra')
    } finally {
      setSavingOrder(false)
    }
  }

  // Handle sort mode change
  const handleSortModeChange = async (mode) => {
    setSortMode(mode)
    // Reload will happen via useEffect
  }

  // Start editing a project description
  const startEditingDescription = (project) => {
    setEditingDescriptionId(project.drive_id)
    setEditingDescriptionText(project.description || '')
  }

  // Cancel editing description
  const cancelEditingDescription = () => {
    setEditingDescriptionId(null)
    setEditingDescriptionText('')
  }

  // Save project description
  const saveProjectDescription = async () => {
    if (!editingDescriptionId) return

    setSavingDescription(true)
    try {
      const response = await driveService.updateProjectDescription(
        editingDescriptionId,
        editingDescriptionText.trim()
      )

      if (response.status === 'success') {
        toast.success('Đã lưu mô tả!')
        // Update local state
        setProjectDescriptions(prev =>
          prev.map(p =>
            p.drive_id === editingDescriptionId
              ? { ...p, description: editingDescriptionText.trim() || null }
              : p
          )
        )
        cancelEditingDescription()
      } else {
        toast.error(response.message || 'Lưu thất bại')
      }
    } catch (error) {
      toast.error('Có lỗi xảy ra')
    } finally {
      setSavingDescription(false)
    }
  }

  useEffect(() => {
    loadConfig()
    loadProjects()
    loadPauseStatus()
    loadFolderTree()
    loadProjectDescriptions()

    // Cleanup EventSource on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
      if (quickSyncEventSourceRef.current) {
        quickSyncEventSourceRef.current.close()
      }
    }
  }, [loadConfig, loadProjects, loadPauseStatus, loadFolderTree, loadProjectDescriptions])

  // Auto-scroll log container
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [syncProgress.logs])

  // Update config
  const handleUpdateConfig = async (e) => {
    e.preventDefault()

    if (!formData.root_folder_id.trim()) {
      toast.error('Vui lòng nhập Folder ID')
      return
    }

    setLoading(true)
    try {
      const response = await driveService.updateConfig(formData.root_folder_id.trim())
      if (response.status === 'success') {
        setConfig(response.data)
        toast.success('Đã cập nhật cấu hình!')
      } else {
        toast.error(response.message || 'Cập nhật thất bại')
      }
    } catch (error) {
      toast.error('Có lỗi xảy ra')
    } finally {
      setLoading(false)
    }
  }

  // Add log entry
  const addLog = (type, message, details = {}) => {
    const timestamp = new Date().toLocaleTimeString('vi-VN')
    setSyncProgress(prev => ({
      ...prev,
      logs: [...prev.logs, { type, message, timestamp, ...details }]
    }))
  }

  // Track error and check if should auto-stop
  const trackError = () => {
    const now = Date.now()

    // Reset counter nếu lỗi cuối đã quá lâu
    if (lastErrorTimeRef.current && (now - lastErrorTimeRef.current) > ERROR_RESET_INTERVAL) {
      consecutiveErrorsRef.current = 0
    }

    consecutiveErrorsRef.current += 1
    lastErrorTimeRef.current = now

    // Kiểm tra nếu đạt ngưỡng lỗi liên tiếp
    if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
      return true // Nên dừng
    }
    return false
  }

  // Reset error counter
  const resetErrorCounter = () => {
    consecutiveErrorsRef.current = 0
    lastErrorTimeRef.current = null
  }

  // Sync drive with streaming progress
  const handleSync = async () => {
    // Reset progress và error counter
    setSyncing(true)
    resetErrorCounter()
    setSyncProgress({
      percent: 0,
      current: 0,
      total: 0,
      currentItem: '',
      logs: []
    })

    // Get token for SSE
    const token = localStorage.getItem('access_token')
    if (!token) {
      toast.error('Vui lòng đăng nhập lại')
      setSyncing(false)
      return
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    // Create EventSource for SSE
    const baseUrl = import.meta.env.VITE_API_URL || ''
    const streamUrl = `${baseUrl}/drive/admin/sync/stream?token=${encodeURIComponent(token)}`

    try {
      addLog('info', 'Đang kết nối tới server...')

      const eventSource = new EventSource(streamUrl)
      eventSourceRef.current = eventSource

      // Connection opened
      eventSource.onopen = () => {
        addLog('success', 'Đã kết nối SSE thành công!')
        resetErrorCounter() // Reset khi kết nối thành công
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
            case 'start':
              addLog('info', data.message)
              resetErrorCounter()
              break

            case 'checking':
              addLog('info', data.message)
              if (data.estimated_total) {
                setSyncProgress(prev => ({
                  ...prev,
                  total: data.estimated_total
                }))
              }
              break

            case 'progress':
              setSyncProgress(prev => ({
                ...prev,
                percent: data.percent,
                current: data.current,
                total: data.total,
                currentItem: data.item_name
              }))

              // Log các event quan trọng
              if (data.action === 'added' || data.action === 'updated' || data.current % 20 === 0) {
                const actionText = {
                  added: '[+] Thêm mới',
                  updated: '[~] Cập nhật',
                  unchanged: '[-] Không đổi'
                }[data.action] || data.action

                addLog(
                  data.action === 'added' ? 'success' : data.action === 'updated' ? 'warning' : 'muted',
                  `${actionText}: ${data.item_name}`,
                  { path: data.path, itemType: data.item_type }
                )
              }
              resetErrorCounter() // Reset khi có progress
              break

            case 'deleting':
              addLog('warning', data.message)
              break

            case 'complete':
              addLog('success', `Hoàn tất! Tổng: ${data.total_items} items`)
              if (data.stats) {
                addLog('info', `Thêm: ${data.stats.added} | Cập nhật: ${data.stats.updated} | Giữ nguyên: ${data.stats.unchanged} | Xóa: ${data.stats.deleted}`)
              }
              setSyncProgress(prev => ({
                ...prev,
                percent: 100,
                current: data.total_items,
                total: data.total_items
              }))
              toast.success(`Đồng bộ hoàn tất! ${data.total_items} items`)
              eventSource.close()
              setSyncing(false)
              loadConfig()
              loadProjects()
              break

            case 'error':
              addLog('error', data.message)

              // Kiểm tra có nên auto-stop không
              const shouldStop = trackError()
              if (shouldStop) {
                addLog('error', `Đã xảy ra ${MAX_CONSECUTIVE_ERRORS} lỗi liên tiếp. Tự động dừng đồng bộ.`)
                toast.error('Đồng bộ bị dừng do lỗi liên tiếp từ Google API')
                eventSource.close()
                setSyncing(false)
              } else {
                toast.error(data.message)
                // Không đóng ngay, để backend tiếp tục retry nếu có thể
              }
              break

            case 'locked':
              addLog('warning', data.message)
              toast.error(data.message)
              if (data.started_at) {
                addLog('info', `Bắt đầu từ: ${new Date(data.started_at).toLocaleTimeString('vi-VN')}`)
              }
              eventSource.close()
              setSyncing(false)
              break

            case 'cancelled':
              addLog('warning', data.message)
              toast.info('Đồng bộ đã bị huỷ')
              eventSource.close()
              setSyncing(false)
              loadConfig()
              break

            case 'paused':
              addLog('warning', `⚠️ ${data.message}`)
              addLog('info', `Đã xử lý: ${data.processed_count} items`)
              if (data.stats) {
                addLog('info', `Thêm: ${data.stats.added} | Cập nhật: ${data.stats.updated} | Giữ nguyên: ${data.stats.unchanged}`)
              }
              toast.error('Đồng bộ tạm dừng do lỗi Google API. Nhấn "Tiếp tục" để thử lại.', {
                duration: 5000
              })
              setPaused(true)
              setCanResume(true)
              eventSource.close()
              setSyncing(false)
              loadConfig()
              break

            default:
              addLog('info', JSON.stringify(data))
          }
        } catch (parseError) {
          console.error('Failed to parse SSE data:', parseError)
        }
      }

      eventSource.onerror = (error) => {
        console.error('SSE Error:', error)
        const readyState = eventSource.readyState
        const stateText = {
          0: 'CONNECTING',
          1: 'OPEN',
          2: 'CLOSED'
        }[readyState] || readyState

        addLog('error', `Lỗi SSE (trạng thái: ${stateText})`)

        // Kiểm tra có nên auto-stop không
        const shouldStop = trackError()

        if (readyState === 2 || shouldStop) {
          if (shouldStop) {
            addLog('error', `Đã xảy ra ${MAX_CONSECUTIVE_ERRORS} lỗi liên tiếp. Tự động dừng.`)
            toast.error('Đồng bộ bị dừng do lỗi liên tiếp')
          } else {
            addLog('warning', 'Kết nối đã đóng, chuyển sang chế độ không streaming...')
            toast.error('Streaming lỗi, đang thử lại...')
          }
          eventSource.close()
          setSyncing(false)

          // Chỉ fallback nếu chưa đạt ngưỡng lỗi
          if (!shouldStop) {
            handleSyncFallback()
          }
        }
      }

    } catch (error) {
      console.error('Failed to create EventSource:', error)
      addLog('error', `Không thể tạo EventSource: ${error.message}`)
      toast.error('Không thể kết nối streaming')

      const shouldStop = trackError()
      if (!shouldStop) {
        handleSyncFallback()
      } else {
        setSyncing(false)
      }
    }
  }

  // Resume paused sync
  const handleResumeSync = async () => {
    addLog('info', 'Đang chuẩn bị tiếp tục đồng bộ...')

    try {
      const response = await driveService.resumeSync()
      if (response.status === 'success') {
        addLog('success', 'Đã sẵn sàng tiếp tục. Bắt đầu đồng bộ lại...')
        setPaused(false)
        setCanResume(false)
        // Trigger new sync
        handleSync()
      } else {
        addLog('error', response.message || 'Không thể tiếp tục đồng bộ')
        toast.error(response.message || 'Không thể tiếp tục đồng bộ')
      }
    } catch (error) {
      addLog('error', 'Lỗi khi tiếp tục đồng bộ')
      toast.error('Lỗi khi tiếp tục đồng bộ')
    }
  }

  // Quick sync a specific folder
  const handleQuickSync = () => {
    if (!selectedFolderId) {
      toast.error('Vui lòng chọn thư mục cần đồng bộ')
      return
    }

    if (!accessToken) {
      toast.error('Phiên đăng nhập hết hạn')
      return
    }

    // Reset progress
    setSyncProgress({
      percent: 0,
      current: 0,
      total: 0,
      currentItem: '',
      logs: []
    })
    setSyncing(true)
    resetErrorCounter()

    addLog('info', `Bắt đầu đồng bộ nhanh folder: ${selectedFolderName}`)

    // Connect to SSE endpoint
    const sseUrl = driveService.getQuickSyncStreamUrl(selectedFolderId, accessToken)
    const eventSource = new EventSource(sseUrl)
    quickSyncEventSourceRef.current = eventSource

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        switch (data.type) {
          case 'start':
            addLog('info', data.message)
            break

          case 'counting':
            addLog('info', data.message)
            break

          case 'counted':
            addLog('success', `Tìm thấy ${data.total_in_drive} items`)
            break

          case 'checking':
            addLog('info', data.message)
            break

          case 'progress':
            setSyncProgress(prev => ({
              ...prev,
              percent: data.percent,
              current: data.current,
              total: data.total,
              currentItem: data.item_name
            }))
            break

          case 'deleting':
            addLog('warning', data.message)
            break

          case 'complete':
            addLog('success', data.message)
            if (data.stats) {
              addLog('info', `Thêm: ${data.stats.added} | Cập nhật: ${data.stats.updated} | Giữ nguyên: ${data.stats.unchanged} | Xóa: ${data.stats.deleted}`)
            }
            toast.success(`Đồng bộ "${data.folder_name}" thành công!`)
            eventSource.close()
            setSyncing(false)
            loadConfig()
            loadFolderTree()
            break

          case 'locked':
            addLog('warning', data.message)
            toast.error(data.message)
            eventSource.close()
            setSyncing(false)
            break

          case 'error':
            addLog('error', data.message)
            toast.error(data.message)
            eventSource.close()
            setSyncing(false)
            loadConfig()
            break

          default:
            addLog('info', JSON.stringify(data))
        }
      } catch (parseError) {
        console.error('Failed to parse SSE data:', parseError)
      }
    }

    eventSource.onerror = () => {
      eventSource.close()
      setSyncing(false)
      addLog('error', 'Mất kết nối với server')
    }
  }

  // Stop/Reset sync
  const handleStopSync = async () => {
    // Close EventSource if connected
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    if (quickSyncEventSourceRef.current) {
      quickSyncEventSourceRef.current.close()
      quickSyncEventSourceRef.current = null
    }

    addLog('warning', 'Đang dừng đồng bộ...')

    try {
      const response = await driveService.resetSyncStatus()
      if (response.status === 'success') {
        addLog('success', 'Đã dừng đồng bộ!')
        toast.success('Đã dừng đồng bộ!')
        await loadConfig()
      } else {
        addLog('error', response.message || 'Không thể dừng đồng bộ')
        toast.error(response.message || 'Không thể dừng đồng bộ')
      }
    } catch (error) {
      addLog('error', 'Lỗi khi dừng đồng bộ')
      toast.error('Lỗi khi dừng đồng bộ')
    } finally {
      setSyncing(false)
      setPaused(false)
      setCanResume(false)
      resetErrorCounter()
    }
  }

  // Fallback sync (non-streaming)
  const handleSyncFallback = async () => {
    setSyncing(true)
    addLog('info', 'Đang đồng bộ (chế độ không streaming)...')

    try {
      const response = await driveService.syncDrive()
      if (response.status === 'success') {
        addLog('success', response.message || 'Đồng bộ thành công!')
        if (response.data?.stats) {
          addLog('info', `Thêm: ${response.data.stats.added} | Cập nhật: ${response.data.stats.updated} | Giữ nguyên: ${response.data.stats.unchanged} | Xóa: ${response.data.stats.deleted}`)
        }
        toast.success(response.message || 'Đồng bộ thành công!')
        await loadConfig()
        await loadProjects()
      } else {
        addLog('error', response.message || 'Đồng bộ thất bại')

        // Kiểm tra auto-stop
        const shouldStop = trackError()
        if (shouldStop) {
          addLog('error', `Đã xảy ra ${MAX_CONSECUTIVE_ERRORS} lỗi liên tiếp. Vui lòng kiểm tra cấu hình.`)
        }
        toast.error(response.message || 'Đồng bộ thất bại')
      }
    } catch (error) {
      addLog('error', 'Có lỗi xảy ra khi đồng bộ')
      toast.error('Có lỗi xảy ra khi đồng bộ')
    } finally {
      setSyncing(false)
    }
  }

  // Copy folder ID
  const handleCopyFolderId = () => {
    if (config?.root_folder_id) {
      navigator.clipboard.writeText(config.root_folder_id)
      toast.success('Đã copy Folder ID!')
    }
  }

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Chưa đồng bộ'
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: vi })
    } catch {
      return dateStr
    }
  }

  // Get status badge
  const getStatusBadge = (status) => {
    // Check paused state first
    if (paused) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
          <HiOutlinePause className="w-3 h-3" />
          Tạm dừng
        </span>
      )
    }

    switch (status) {
      case 'idle':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <HiOutlineCheck className="w-3 h-3" />
            Sẵn sàng
          </span>
        )
      case 'syncing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <HiOutlineRefresh className="w-3 h-3 animate-spin" />
            Đang đồng bộ
          </span>
        )
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <HiOutlineExclamation className="w-3 h-3" />
            Lỗi
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            Chưa cấu hình
          </span>
        )
    }
  }

  if (loading && !config) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <HiOutlineFolderOpen className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Cài đặt Drive Docs</h1>
              <p className="text-sm text-gray-500">Cấu hình và đồng bộ tài liệu từ Google Drive</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiOutlineDatabase className="w-5 h-5 text-gray-400" />
            Trạng thái đồng bộ
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Trạng thái</p>
              {getStatusBadge(syncing ? 'syncing' : config?.sync_status)}
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Tổng số items</p>
              <p className="text-2xl font-bold text-gray-900">
                {config?.total_items || 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Lần đồng bộ cuối</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(config?.last_sync_at)}
              </p>
            </div>
          </div>

          {/* Error message */}
          {config?.error_message && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{config.error_message}</p>
            </div>
          )}

          {/* Sync Progress - hiển thị khi đang sync hoặc có logs */}
          {(syncing || syncProgress.logs.length > 0) && (
            <div className="mt-4 space-y-3">
              {/* Progress bar - chỉ hiện khi đang sync */}
              {syncing && (
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">
                      {syncProgress.currentItem || 'Đang xử lý...'}
                    </span>
                    <span className="font-medium text-primary-600">
                      {syncProgress.percent}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(syncProgress.percent, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {syncProgress.current} / {syncProgress.total} items
                  </p>
                </div>
              )}

              {/* Log container */}
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">Logs</span>
                {!syncing && syncProgress.logs.length > 0 && (
                  <button
                    onClick={() => setSyncProgress(prev => ({ ...prev, logs: [] }))}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Xoá logs
                  </button>
                )}
              </div>
              <div
                ref={logContainerRef}
                className="bg-gray-900 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs"
              >
                {syncProgress.logs.map((log, index) => (
                  <div
                    key={index}
                    className={clsx(
                      'flex items-start gap-2 py-1',
                      log.type === 'error' && 'text-red-400',
                      log.type === 'success' && 'text-green-400',
                      log.type === 'warning' && 'text-orange-400',
                      log.type === 'muted' && 'text-gray-500',
                      log.type === 'info' && 'text-gray-300'
                    )}
                  >
                    <span className="text-gray-500 flex-shrink-0">[{log.timestamp}]</span>
                    <span className="break-all">{log.message}</span>
                  </div>
                ))}
                {syncProgress.logs.length === 0 && syncing && (
                  <div className="text-gray-500">Đang khởi tạo...</div>
                )}
              </div>
            </div>
          )}

          {/* Paused warning */}
          {paused && canResume && (
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-3">
                <HiOutlinePause className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-orange-800">Đồng bộ đã tạm dừng</p>
                  <p className="text-sm text-orange-700 mt-1">
                    Quá trình đồng bộ bị gián đoạn do lỗi liên tiếp từ Google API.
                    Dữ liệu đã xử lý được lưu lại. Bạn có thể thử tiếp tục hoặc dừng hoàn toàn.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sync buttons */}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {/* Resume button - hiện khi đang pause */}
            {paused && canResume && !syncing && (
              <button
                onClick={handleResumeSync}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                <HiOutlinePlay className="w-5 h-5" />
                Tiếp tục đồng bộ
              </button>
            )}

            {/* Reload button */}
            <button
              onClick={handleSync}
              disabled={syncing || config?.sync_status === 'syncing'}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                syncing || config?.sync_status === 'syncing'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : paused
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
              )}
            >
              <HiOutlineRefresh className={clsx('w-5 h-5', syncing && 'animate-spin')} />
              {syncing ? 'Đang đồng bộ...' : paused ? 'Đồng bộ lại từ đầu' : 'Reload (Đồng bộ lại)'}
            </button>

            {/* Stop button - hiện khi đang sync hoặc pause */}
            {(syncing || config?.sync_status === 'syncing' || paused) && (
              <button
                onClick={handleStopSync}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                  paused && !syncing
                    ? 'bg-gray-500 text-white hover:bg-gray-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                )}
              >
                <HiOutlineStop className="w-5 h-5" />
                {paused && !syncing ? 'Huỷ đồng bộ' : 'Dừng đồng bộ'}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {paused
              ? 'Nhấn "Tiếp tục" để thử đồng bộ lại từ vị trí đã dừng, hoặc "Đồng bộ lại từ đầu" để quét toàn bộ.'
              : `Nhấn để quét lại toàn bộ tài liệu từ Google Drive. Files đã thay đổi sẽ được cập nhật. Hệ thống sẽ tự động tạm dừng nếu gặp ${MAX_CONSECUTIVE_ERRORS} lỗi liên tiếp từ Google API.`
            }
          </p>
        </div>

        {/* Quick Sync Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiOutlineLightningBolt className="w-5 h-5 text-amber-500" />
            Đồng bộ nhanh
          </h2>

          <p className="text-sm text-gray-600 mb-4">
            Chọn một thư mục cụ thể để đồng bộ nhanh thay vì quét toàn bộ Drive.
            Hữu ích khi bạn chỉ cập nhật tài liệu trong một dự án.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <FolderTreeSelect
                folders={folderTree}
                value={selectedFolderId}
                onChange={(folderId, folder) => {
                  setSelectedFolderId(folderId)
                  setSelectedFolderName(folder?.name || '')
                }}
                loading={loadingFolderTree}
                placeholder="Chọn thư mục cần đồng bộ..."
              />
            </div>
            <button
              onClick={handleQuickSync}
              disabled={syncing || !selectedFolderId}
              className={clsx(
                'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap',
                syncing || !selectedFolderId
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              )}
            >
              <HiOutlineLightningBolt className={clsx('w-5 h-5', syncing && 'animate-pulse')} />
              {syncing ? 'Đang đồng bộ...' : 'Đồng bộ nhanh'}
            </button>
          </div>

          {folderTree.length === 0 && !loadingFolderTree && (
            <p className="text-xs text-gray-500 mt-3">
              Chưa có dữ liệu folder. Vui lòng "Reload" toàn bộ trước để tạo cấu trúc thư mục.
            </p>
          )}
        </div>

        {/* Config Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiOutlineCog className="w-5 h-5 text-gray-400" />
            Cấu hình Google Drive
          </h2>

          <form onSubmit={handleUpdateConfig} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Root Folder ID
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.root_folder_id}
                  onChange={(e) => setFormData({ root_folder_id: e.target.value })}
                  placeholder="13v9WdSrWl8t7zpSW5pwGnpp6acng2htl"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
                {config?.root_folder_id && (
                  <button
                    type="button"
                    onClick={handleCopyFolderId}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copy Folder ID"
                  >
                    <HiOutlineClipboardCopy className="w-5 h-5 text-gray-500" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ID của thư mục gốc trên Google Drive chứa tài liệu dự án
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={loading}
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium transition-colors',
                  loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                )}
              >
                {loading ? 'Đang lưu...' : 'Lưu cấu hình'}
              </button>

              {config?.root_folder_id && (
                <a
                  href={`https://drive.google.com/drive/folders/${config.root_folder_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <HiOutlineExternalLink className="w-4 h-4" />
                  Mở trên Drive
                </a>
              )}
            </div>
          </form>
        </div>

        {/* Project Ordering Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <HiOutlineSwitchVertical className="w-5 h-5 text-gray-400" />
              Sắp xếp thứ tự dự án
            </h2>

            {/* Sort mode tabs */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleSortModeChange('manual')}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                  sortMode === 'manual'
                    ? 'bg-white shadow-sm text-primary-600'
                    : 'text-gray-600 hover:text-gray-800'
                )}
              >
                Thủ công
              </button>
              <button
                onClick={() => handleSortModeChange('number')}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                  sortMode === 'number'
                    ? 'bg-white shadow-sm text-primary-600'
                    : 'text-gray-600 hover:text-gray-800'
                )}
              >
                Theo số
              </button>
              <button
                onClick={() => handleSortModeChange('name')}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                  sortMode === 'name'
                    ? 'bg-white shadow-sm text-primary-600'
                    : 'text-gray-600 hover:text-gray-800'
                )}
              >
                A-Z
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            {sortMode === 'manual'
              ? 'Di chuyển lên/xuống để sắp xếp thứ tự hiển thị. Nhấn "Lưu thứ tự" để áp dụng.'
              : sortMode === 'number'
              ? 'Sắp xếp theo số đầu tên (vd: "21. Phúc Gia Tân" → 21). Nhấn "Lưu thứ tự" để lưu làm mặc định.'
              : 'Sắp xếp theo tên A-Z. Nhấn "Lưu thứ tự" để lưu làm mặc định.'
            }
          </p>

          {loadingProjects ? (
            <div className="py-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : projects.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <HiOutlineFolder className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>Chưa có dự án nào. Hãy đồng bộ trước.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((project, index) => (
                <div
                  key={project.drive_id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg group"
                >
                  {/* Order number */}
                  <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>

                  {/* Folder icon */}
                  <HiOutlineFolder className="w-5 h-5 text-amber-500 flex-shrink-0" />

                  {/* Name and file count */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{project.name}</p>
                    <p className="text-xs text-gray-500">{project.file_count || 0} files</p>
                  </div>

                  {/* Move buttons - only show in manual mode */}
                  {sortMode === 'manual' && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => moveProjectUp(index)}
                        disabled={index === 0}
                        className={clsx(
                          'p-1.5 rounded-md transition-colors',
                          index === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                        )}
                        title="Di chuyển lên"
                      >
                        <HiOutlineChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveProjectDown(index)}
                        disabled={index === projects.length - 1}
                        className={clsx(
                          'p-1.5 rounded-md transition-colors',
                          index === projects.length - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                        )}
                        title="Di chuyển xuống"
                      >
                        <HiOutlineChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Save button - show for all modes when there are projects */}
          {projects.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={saveProjectOrder}
                disabled={savingOrder}
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium transition-colors',
                  savingOrder
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                )}
              >
                {savingOrder ? 'Đang lưu...' : 'Lưu thứ tự'}
              </button>
              {sortMode !== 'manual' && (
                <span className="ml-3 text-xs text-gray-500">
                  Lưu thứ tự hiện tại làm mặc định (chế độ Thủ công)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Project Descriptions Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiOutlineDocumentText className="w-5 h-5 text-gray-400" />
            Mô tả dự án
          </h2>

          <p className="text-sm text-gray-500 mb-4">
            Thêm mô tả ngắn cho từng dự án. Mô tả sẽ hiển thị dưới tên folder trên trang Tài liệu.
          </p>

          {loadingDescriptions ? (
            <div className="py-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : projectDescriptions.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <HiOutlineFolder className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>Chưa có dự án nào. Hãy đồng bộ trước.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {projectDescriptions.map((project) => (
                <div
                  key={project.drive_id}
                  className="p-4 bg-gray-50 rounded-lg"
                >
                  {/* Project name row */}
                  <div className="flex items-center gap-3 mb-2">
                    <HiOutlineFolder className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span className="font-medium text-gray-800 flex-1 truncate">
                      {project.name}
                    </span>
                    {editingDescriptionId !== project.drive_id && (
                      <button
                        onClick={() => startEditingDescription(project)}
                        className="p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700 rounded-md transition-colors"
                        title="Chỉnh sửa mô tả"
                      >
                        <HiOutlinePencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Description editing or display */}
                  {editingDescriptionId === project.drive_id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingDescriptionText}
                        onChange={(e) => setEditingDescriptionText(e.target.value)}
                        placeholder="Nhập mô tả ngắn về dự án..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none text-sm resize-none"
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={saveProjectDescription}
                          disabled={savingDescription}
                          className={clsx(
                            'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                            savingDescription
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-primary-600 text-white hover:bg-primary-700'
                          )}
                        >
                          {savingDescription ? 'Đang lưu...' : 'Lưu'}
                        </button>
                        <button
                          onClick={cancelEditingDescription}
                          disabled={savingDescription}
                          className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors"
                        >
                          Hủy
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ml-8">
                      {project.description ? (
                        <p className="text-sm text-gray-600">{project.description}</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Chưa có mô tả</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-700">
            <strong>Lưu ý:</strong> Files sẽ được tự động phân loại dựa trên tên (bảng giá, CSBH, mặt bằng, pháp lý, media...).
            Khi có cập nhật trên Drive, cần nhấn "Reload" để đồng bộ lại. Files đã thay đổi (dựa trên modifiedTime) sẽ được cập nhật tự động.
          </p>
        </div>
      </div>
    </div>
  )
}

export default DriveSettings
